import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FaEllipsisV } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import QuoteStatusPill from "../../../components/QuoteStatusPill";
import { QUOTE_STATUSES, QuoteListItem } from "../../../models/Quote";
import { deleteQuote, getQuotes, linkQuoteProcess, setQuoteStatus } from "../../../services/quoteService";
import { crm } from "../../../theme";
import { TableCard, tdProps, thProps } from "../../process/components/tableStyles";
import LinkProcessModal from "./LinkProcessModal";

const brl = (value?: number | null) =>
  value == null ? "—" : value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const day = (value?: string | null) => (value ? new Date(value).toLocaleDateString("pt-BR") : "—");

/** Aba "Orçamentos": orçamentos salvos a partir do Simulador (api/quote). */
/** Hoje no fuso local, como yyyy-mm-dd (comparável com a data de validade). */
const localTodayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const QuotesTab = () => {
  const todayIso = localTodayIso();
  const navigate = useNavigate();
  const toast = useToast();
  const [quotes, setQuotes] = useState<QuoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [toDelete, setToDelete] = useState<QuoteListItem | null>(null);
  const [toLink, setToLink] = useState<QuoteListItem | null>(null);
  const deleteDialog = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setQuotes(await getQuotes({ search: search.trim() || undefined, status: status || undefined }));
    setLoading(false);
  }, [search, status]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const notifyFailure = (notifications: string[]) =>
    toast({ status: "error", title: notifications[0] ?? "Não foi possível concluir a operação." });

  const changeStatus = async (quote: QuoteListItem, next: string) => {
    const response = await setQuoteStatus(quote.id!, next);
    if (!response.success) return notifyFailure(response.notifications);
    toast({ status: "success", title: `Orçamento ${quote.number}: ${next}.` });
    load();
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const response = await deleteQuote(toDelete.id!);
    deleteDialog.onClose();
    setToDelete(null);
    if (!response.success) return notifyFailure(response.notifications);
    toast({ status: "success", title: "Orçamento excluído." });
    load();
  };

  const link = async (processId: string): Promise<string[] | null> => {
    const response = await linkQuoteProcess(toLink!.id!, processId);
    if (!response.success) return response.notifications;
    toast({ status: "success", title: "Processo vinculado." });
    load();
    return null;
  };

  const unlink = async (quote: QuoteListItem) => {
    const response = await linkQuoteProcess(quote.id!, null);
    if (!response.success) return notifyFailure(response.notifications);
    load();
  };

  return (
    <Box
      width="100%"
      bg={crm.surface}
      border={`1px solid ${crm.border}`}
      borderRadius={crm.radius}
      boxShadow={crm.shadowSm}
      p={{ base: "16px", md: "24px" }}
      mb="32px"
    >
      <Flex gap="12px" mb="16px" wrap="wrap">
        <Input
          size="sm"
          maxW="320px"
          bg="white"
          placeholder="Buscar por número, cliente ou produto"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select size="sm" maxW="180px" bg="white" placeholder="Todos os status" value={status} onChange={(e) => setStatus(e.target.value)}>
          {QUOTE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <Button size="sm" variant="outline" ml={{ md: "auto" }} onClick={() => navigate("/simulador")}>
          Novo orçamento no simulador
        </Button>
      </Flex>

      {loading ? (
        <Flex justify="center" py="32px">
          <Spinner color={crm.brand} />
        </Flex>
      ) : quotes.length === 0 ? (
        <Text textAlign="center" py="32px" color={crm.textMuted} fontSize="14px">
          Nenhum orçamento encontrado. Salve uma simulação do Simulador como orçamento para vê-la aqui.
        </Text>
      ) : (
        <TableCard>
          <Thead>
            <Tr>
              <Th {...thProps}>Número</Th>
              <Th {...thProps}>Cliente / lead</Th>
              <Th {...thProps}>Produto</Th>
              <Th {...thProps}>Status</Th>
              <Th {...thProps}>Versão</Th>
              <Th {...thProps} isNumeric>
                Custo final
              </Th>
              <Th {...thProps}>Validade</Th>
              <Th {...thProps}>Processo</Th>
              <Th {...thProps} />
            </Tr>
          </Thead>
          <Tbody>
            {quotes.map((q) => {
              // Válido até o fim do dia informado: compara só a data (yyyy-mm-dd), sem hora
              const expired = !!q.validUntil && q.validUntil.slice(0, 10) < todayIso && q.status !== "Aprovado";
              return (
                <Tr key={q.id}>
                  <Td {...tdProps} fontWeight="600" whiteSpace="nowrap">
                    <Text
                      as="button"
                      color={crm.brand}
                      fontWeight="600"
                      onClick={() => navigate(`/simulador?quote=${q.id}`)}
                    >
                      {q.number}
                    </Text>
                  </Td>
                  <Td {...tdProps}>
                    <Text fontWeight="500">{q.clientName}</Text>
                    {q.prospectName && (
                      <Text fontSize="11px" color={crm.textMuted}>
                        Lead: {q.prospectName}
                      </Text>
                    )}
                  </Td>
                  <Td {...tdProps}>{q.product || "—"}</Td>
                  <Td {...tdProps}>
                    <QuoteStatusPill status={q.status} />
                  </Td>
                  <Td {...tdProps} whiteSpace="nowrap">
                    v{q.currentVersion}
                    {q.approvedVersion ? (
                      <Text as="span" fontSize="11px" color={crm.textMuted}>
                        {" "}
                        (aprov. v{q.approvedVersion})
                      </Text>
                    ) : null}
                  </Td>
                  <Td {...tdProps} isNumeric whiteSpace="nowrap" fontWeight="600">
                    {brl(q.finalCostBrl)}
                  </Td>
                  <Td {...tdProps} whiteSpace="nowrap" color={expired ? crm.danger : undefined}>
                    {day(q.validUntil)}
                  </Td>
                  <Td {...tdProps} whiteSpace="nowrap">
                    {q.processId ? q.processRef || "Vinculado" : "—"}
                  </Td>
                  <Td {...tdProps}>
                    <Menu placement="bottom-end">
                      <MenuButton as={IconButton} aria-label="Ações" size="sm" variant="ghost" icon={<FaEllipsisV />} />
                      <MenuList fontSize="13px">
                        <MenuItem onClick={() => navigate(`/simulador?quote=${q.id}`)}>Abrir no simulador</MenuItem>
                        {QUOTE_STATUSES.filter((s) => s !== q.status).map((s) => (
                          <MenuItem key={s} onClick={() => changeStatus(q, s)}>
                            Marcar como {s.toLowerCase()}
                            {s === "Aprovado" ? ` (v${q.currentVersion})` : ""}
                          </MenuItem>
                        ))}
                        {q.processId ? (
                          <MenuItem onClick={() => unlink(q)}>Desvincular processo</MenuItem>
                        ) : (
                          <MenuItem onClick={() => setToLink(q)}>Vincular processo</MenuItem>
                        )}
                        <MenuItem
                          color={crm.danger}
                          onClick={() => {
                            setToDelete(q);
                            deleteDialog.onOpen();
                          }}
                        >
                          Excluir
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </TableCard>
      )}

      <LinkProcessModal isOpen={Boolean(toLink)} onClose={() => setToLink(null)} quoteNumber={toLink?.number} onConfirm={link} />

      <AlertDialog isOpen={deleteDialog.isOpen} leastDestructiveRef={cancelRef} onClose={deleteDialog.onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius={crm.radius}>
            <AlertDialogHeader fontSize="lg" fontWeight="700">
              Excluir orçamento
            </AlertDialogHeader>
            <AlertDialogBody>
              Excluir o orçamento {toDelete?.number}? As versões salvas e o vínculo com o processo serão removidos.
            </AlertDialogBody>
            <AlertDialogFooter gap="8px">
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={confirmDelete}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default QuotesTab;
